import {
  Box,
  HStack,
  Icon,
  IconButton,
  InvestorBadge,
  Stack,
  SuitabilityBadge,
  Text,
  VStack,
} from '@genial/components';
import { BoxRadius, Color } from '@genial/design-tokens';
import { useBreakpointValue, Utils } from '@genial/utils';
import React from 'react';

import { CustomerWalletPoints } from '../../../../../components/CustomerWalletPoints';

import { BlockStatusTooltip } from './components/BlockStatusTooltip';

import { InfoPanelTypes } from '.';

export const InfoPanelAccount: React.FC<InfoPanelTypes.PanelAccountProps> = ({
  isLoading,
  customer,
  accountNavigate,
}) => {
  const isMobile = useBreakpointValue({ base: true, sm: false });
  const isTablet = useBreakpointValue({ base: false, sm: true, lg: false });
  const mainD = useBreakpointValue({ base: 'row', sm: 'column' } as const);
  const secondaryD = useBreakpointValue({ base: 'column', sm: 'row' } as const);
  const textAlign = useBreakpointValue({ base: 'right', sm: 'left' } as const);
  const textVariant = useBreakpointValue({ base: undefined, sm: 'bold' } as const);

  return (
    <Stack
      direction={secondaryD}
      space="stack.nano"
      width="full"
      backgroundColor={Color.background}
      p="inline.xxxs"
      borderRadius={BoxRadius.sm}>
      <VStack w="full">
        <Stack direction={secondaryD}>
          <Box
            width={{ base: 'full', sm: '20%' }}
            alignItems={{ base: 'flex-start' }}
            justifyContent={{ base: 'flex-start', sm: 'center' }}
            mb={{ base: 'stack.nano', sm: undefined }}>
            <Text
              fontSize="lg"
              fontWeight="semibold"
              color={{ base: 'neutral.black', sm: 'neutral.dark' }}>
              Dados Financeiros
            </Text>
          </Box>
          <Stack flex={1} direction={secondaryD} space={{ base: 'stack.nano', sm: 'stack.quarck' }}>
            <Stack direction={mainD} space="stack.quarck" flex={1}>
              <Text fontSize="md" variant={textVariant}>
                Investidor
              </Text>
              <Box
                flex={1}
                alignItems={{ base: 'flex-end', sm: 'flex-start' }}
                justifyContent="center">
                {isLoading ? (
                  <Text isLoading w="full" />
                ) : (
                  <InvestorBadge
                    investorType={customer?.investorProfile.investmentProfileNormalized}
                  />
                )}
              </Box>
            </Stack>
            <Stack direction={mainD} space="stack.quarck" flex={1}>
              <Text fontSize="md" variant={textVariant}>
                Suitability
              </Text>
              <Box
                flex={1}
                alignItems={{ base: 'flex-end', sm: 'flex-start' }}
                justifyContent="center">
                {isLoading ? (
                  <Text isLoading w="full" />
                ) : (
                  <SuitabilityBadge label={customer?.investorProfile.suitabilityNormalized || ''} />
                )}
              </Box>
            </Stack>
            <Stack direction={mainD} space="stack.quarck" flex={1}>
              <Text fontSize="md" variant={textVariant}>
                Conta digital
              </Text>
              <Text isLoading={isLoading} fontSize="md" textAlign={textAlign} flex={1}>
                {customer?.accountAndEarnings.digitalAccountNormalized}
              </Text>
            </Stack>
            <Stack direction={mainD} space="stack.quarck" flex={1}>
              <Text fontSize="md" variant={textVariant}>
                Cadastro
              </Text>
              <BlockStatusTooltip customer={customer} isLoading={isLoading}>
                <HStack
                  space="stack.nano"
                  flex={1}
                  justifyContent={{ base: 'flex-end', sm: 'flex-start' }}>
                  <Text isLoading={isLoading} fontSize="md" textAlign={textAlign}>
                    {customer?.accountAndEarnings.statusNormalized.textNormalized}
                  </Text>
                  {!isLoading && customer && (
                    <Icon
                      testID="account-status-icon"
                      name={customer?.accountAndEarnings.statusNormalized.iconNormalized}
                      size={customer?.accountAndEarnings.statusNormalized.iconSizeNormalized}
                      color={customer?.accountAndEarnings.statusNormalized.iconColorNormalized}
                    />
                  )}
                </HStack>
              </BlockStatusTooltip>
            </Stack>
            {!isMobile && (
              <IconButton
                onPress={accountNavigate}
                icon="ChevronRight"
                testId="goto-financial-data"
                iconColor="brand.primary.principal"
                iconSize="xs"
              />
            )}
          </Stack>
        </Stack>
        {isTablet && (
          <HStack w="full" mt="stack.xxxs">
            <Box width={{ base: 'full', sm: '20%' }} />
            <Stack
              flex={1}
              direction={secondaryD}
              space={{ base: 'stack.nano', sm: 'stack.quarck' }}>
              <Stack direction={mainD} space="stack.quarck" flex={1}>
                <Text fontSize="md" variant={textVariant}>
                  Disp. para investir
                </Text>
                <Text isLoading={isLoading} fontSize="md" textAlign={textAlign} flex={1}>
                  {Utils.Mask.Money(customer?.accountAndEarnings.availableToInvest)}
                </Text>
              </Stack>
              <Stack direction={mainD} space="stack.quarck" flex={1}>
                <Text fontSize="md" variant={textVariant}>
                  Patrimônio total
                </Text>
                <Text isLoading={isLoading} fontSize="md" textAlign={textAlign} flex={1}>
                  {customer?.accountAndEarnings.totalNetWorthNormalized}
                </Text>
              </Stack>
              <Stack direction={mainD} space="stack.quarck" flex={1}>
                <Text fontSize="md" variant={textVariant}>
                  Saldo em conta
                </Text>
                <Text isLoading={isLoading} fontSize="md" textAlign={textAlign} flex={1}>
                  {Utils.Mask.Money(customer?.accountAndEarnings.availableBalance)}
                </Text>
              </Stack>
              <Stack direction={mainD} space="stack.quarck" flex={1}>
                <Text fontSize="md" variant={textVariant}>
                  Pts da carteira
                </Text>
                {!isLoading && customer ? (
                  <CustomerWalletPoints
                    preventPadding
                    accountNumber={customer.additionalAccounts[0]?.accountNumber}
                  />
                ) : (
                  <Text isLoading={isLoading} />
                )}
              </Stack>
              {!isMobile && <IconButton testId="hidden-button" icon="ChevronRight" iconSize="xs" />}
            </Stack>
          </HStack>
        )}
      </VStack>
    </Stack>
  );
};
